import { Box, Flex, Link, Text, Tooltip } from '@chakra-ui/react';
import { FilePayload } from '@worknet/models';

import { formatFileType } from '../../utils/formatFileType';
import { resolveLinkTarget } from '../../utils/linkTarget';

import { getFileIcon } from './fileIconUtils';

function truncateFilename(filename: string, maxLength = 18): string {
  if (filename.length <= maxLength) {
    return filename;
  }

  const lastDotIndex = filename.lastIndexOf('.');

  if (lastDotIndex === -1 || lastDotIndex === 0) {
    return `${filename.substring(0, maxLength - 3)}...`;
  }

  const extension = filename.substring(lastDotIndex);
  const nameWithoutExtension = filename.substring(0, lastDotIndex);

  if (extension.length > 6) {
    return `${nameWithoutExtension.substring(0, maxLength - 3)}...`;
  }

  const charsToKeep = maxLength - extension.length - 3;

  return `${nameWithoutExtension.substring(0, Math.max(1, charsToKeep))}...${extension}`;
}

export interface MessageAttachmentProps {
  file: FilePayload;
  inUserMessage?: boolean;
}

export default function MessageAttachment({ file, inUserMessage = false }: MessageAttachmentProps) {
  const renderFileIcon = () => getFileIcon(file.fileType);

  const truncatedName = truncateFilename(file.fileName);
  const fileType = formatFileType(file.fileType);

  return (
    <Link
      href={file.url}
      {...resolveLinkTarget(file.url)}
      textDecoration="none"
      _hover={{ textDecoration: 'none' }}
    >
      <Flex
        alignItems="center"
        bg={inUserMessage ? 'rgba(255, 255, 255, 0.2)' : 'gray.100'}
        gap={2}
        px={'10px'}
        py={'10px'}
        borderRadius="6px"
        w="fit-content"
        maxW="180px"
        h={12}
      >
        <Box flexShrink={0}>{renderFileIcon()}</Box>
        <Box overflow="hidden">
          <Tooltip label={file.fileName} placement="top" hasArrow openDelay={500}>
            <Text
              fontSize="10px"
              color={inUserMessage ? 'white' : 'greyscale.950'}
              mb={'2px'}
              w="full"
              noOfLines={1}
            >
              {truncatedName}
            </Text>
          </Tooltip>
          <Text
            fontSize="8px"
            lineHeight="9.6px"
            color={inUserMessage ? 'whiteAlpha.800' : 'greyscale.800'}
          >
            {fileType}
          </Text>
        </Box>
      </Flex>
    </Link>
  );
}
